import React, { Suspense, useCallback, useEffect, useRef } from 'react'
import { useStore } from 'effector-react'
import { Outlet, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'

import apiService from 'src/services/api'
import {
  $isMaintenanceModeActive,
  $isNotAllowedServer,
  setIsMaintenanceModeActive,
  setIsNotAllowedServer,
  setDomainsList,
  $isLoadingUser,
  $user,
  saveIsUserLoading,
  saveUserInStore
} from 'src/state/store'

import LoadingIndicator from '../loading-indicator'

import { responseSuccessInterceptor } from '../../services/interceptors'
import { SUPPORTED_CHAIN, UserRoles } from '../../constants'
import { WrongNetwork } from '../WrongNetwork'
import useMetaMask from '../../hooks/useMetaMask'
import Header from '../header'
import { usePrev } from '../../hooks/usePrev'

const TIMEOUT = 60000

const AuthLayout = () => {
  const { isAuth, account } = useMetaMask()
  const isLoading = useStore($isLoadingUser)
  const isNotAllowedServer = useStore($isNotAllowedServer)
  const isMaintenanceModeActive = useStore($isMaintenanceModeActive)
  const { chainId } = useMetaMask()

  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')

  const prevIsMaintenance = usePrev(isMaintenanceModeActive)

  const user = useStore($user)

  const getDomains = useCallback(async () => {
    try {
      const res = await apiService.getMyDomainsList()
      setDomainsList(
        res.data.map((domain: { id: number; name: string }) => ({
          ...domain,
          name: `.${domain.name}`
        }))
      )
    } catch (e) {
      setDomainsList([{ id: 4, name: '.aaa' }])
    }
  }, [])

  const getUserProfile = useCallback(async () => {
    try {
      saveIsUserLoading(!user?.id)
      const res = await apiService.getUser()

      saveUserInStore(res.data)
      if (!res.data.deactivated && res.data.domains.length) {
        await getDomains()
      }

      if (redirectTo) {
        navigate('/')
      }
    } catch (e) {
      Promise.resolve()
    } finally {
      saveIsUserLoading(false)
    }
  }, [navigate, redirectTo, pathname])

  useEffect(() => {
    if (isAuth && account) {
      getUserProfile()
    }
  }, [getUserProfile, isAuth, account])

  useEffect(() => {
    if (isAuth && prevIsMaintenance && !isMaintenanceModeActive) {
      getUserProfile()
    }
  }, [isMaintenanceModeActive, prevIsMaintenance, isAuth, getUserProfile])

  const intervalRef = useRef<ReturnType<typeof setInterval>>()

  const handleClearInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current as NodeJS.Timeout)
    }
  }

  const handleCheckIsBEHealth = async (onlyMaintenance = false) => {
    if (!onlyMaintenance) {
      await apiService
        .isBEHealth()
        .then(() => {
          setIsNotAllowedServer(false)
        })
        .catch(() => {
          setIsNotAllowedServer(true)
        })
    }

    await apiService.getLastMaintenanceState().then((data) => {
      const successData = responseSuccessInterceptor(data)
      setIsMaintenanceModeActive(!!successData.message)
      if (!successData.message) {
        handleClearInterval()
      }
    })
  }

  useEffect(() => {
    if (isNotAllowedServer || isMaintenanceModeActive) {
      intervalRef.current = setInterval(
        () => handleCheckIsBEHealth(isMaintenanceModeActive),
        TIMEOUT
      )
    }

    return () => handleClearInterval()
  }, [isNotAllowedServer, isMaintenanceModeActive])

  useEffect(() => {
    if (!isNotAllowedServer) {
      handleCheckIsBEHealth()
    }
  }, [])

  if (chainId && SUPPORTED_CHAIN !== chainId) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <Header />
        <WrongNetwork />
      </Box>
    )
  }

  return (
    <Suspense fallback={<LoadingIndicator />}>
      {isAuth ? isLoading ? <LoadingIndicator /> : <Outlet /> : <Outlet />}
    </Suspense>
  )
}

export default AuthLayout
